import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
    GetJobTypes,
    GetAllUsersInternalRoles,
    GetAccountingStatus,
    GetJobTechniques,
    GetJobIssues,
    GetEstimateStatus,
    CreateNewJobs,
    GetJobDetailsbyUserId,
    DeleteJobsById,
    UpdateJobsById,
    GetJobDetailsById
} from '../../services/accountService';
import AgGrid from '../common/AgGrid';
import FilesTab from './FilesTab';
import ListSummaryTab from './ListSummaryTab';
import Icon from '../common/Icon';
import { AisScreenLoader } from '../../utils/AisScreenLoader';
import CostSummary from './CostSummary';
import SuccessMessage from '../common/SuccessMessage';
import {
    hasPermission,
    canViewFieldOnScreen,
    canEditFieldOnScreen,
    isListAgent,
    isRestrictedUser,
} from '../../utils/PermissionUtils';

const JobDetails = () => {
    const { jobId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const hasFullJobAccess = hasPermission('Job.View');
    const hasFilesOnlyAccess = hasPermission('Job.Files.View');

    const isViewOnlyRestrictedRole = isRestrictedUser();

    const isListAgentUser = isListAgent();

    const hasListAgentViewAccess = hasFullJobAccess || isListAgentUser;

    const canViewJobInfo = hasListAgentViewAccess && !isViewOnlyRestrictedRole &&
        (isListAgentUser || canViewFieldOnScreen('Jobs', 'JobDetail', 'fullScreen'));
    const canEditJobInfo = hasFullJobAccess && canEditFieldOnScreen('Jobs', 'JobDetail', 'fullScreen') && !isViewOnlyRestrictedRole && !isListAgentUser;
    const canViewListSummaryTab = hasListAgentViewAccess && canViewFieldOnScreen('Jobs', 'ListSummary', 'fullTab') && !isViewOnlyRestrictedRole;
    const canViewCostSummaryTab = hasFullJobAccess && canViewFieldOnScreen('Jobs', 'CostSummary', 'fullTab') && !isListAgentUser
    const canEditCostSummaryTab = hasFullJobAccess
        && canEditFieldOnScreen('Jobs', 'CostSummary', 'fullTab')
        && !isViewOnlyRestrictedRole && !isListAgentUser;

    const canViewFilesTab = (hasFullJobAccess || hasFilesOnlyAccess) && canViewFieldOnScreen('Jobs', 'FilesTab', 'fullTab') && !isViewOnlyRestrictedRole && !isListAgentUser;


    const canCreateOrCopyJob = hasPermission('Job.Edit');

    const canDeleteJob = hasPermission('Job.Delete');
    const canManageJob = canCreateOrCopyJob || canDeleteJob;


    const allowedTabs = [
        canViewListSummaryTab && 'list-summary',
        canViewCostSummaryTab && 'cost-summary',
        canViewFilesTab && 'files'
    ].filter(Boolean);


    const canViewAnything = canViewJobInfo || allowedTabs.length > 0;


    useEffect(() => {
        if (!canViewAnything) {
            navigate('/unauthorized', { replace: true });
        }
    }, [canViewAnything, navigate]);

    const [successMessage, setSuccessMessage] = useState('');
    const [messageType, setMessageType] = useState('success');


    const [jobData, setJobData] = useState({
        jobName: '',
        jobId: '',
        numericJobId: null,
        jobType: null,
        mailDate: '',
        productionManager: null,
        copywriter: '',
        accountingStatus: 1,
        coordinator: null,
        technique: null,
        issue: null,
        inventory: '',
        estimateStatus: null,
        notes: '',
        clientID: null
    });


    const [isCopyMode, setIsCopyMode] = useState(false);
    const [originalJobId, setOriginalJobId] = useState('');

    const [loading, setLoading] = useState(true);
    const [isNewJob, setIsNewJob] = useState(false);
    const [isJobInfoCollapsed, setIsJobInfoCollapsed] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);


    const [mailDateError, setMailDateError] = useState('');
    const [jobNameError, setJobNameError] = useState('');
    const [jobTypeError, setJobTypeError] = useState('');
    const [productionManagerError, setProductionManagerError] = useState('');
    const [accountingStatusError, setAccountingStatusError] = useState('');
    const [estimateStatusError, setEstimateStatusError] = useState('');
    const [jobIdError, setJobIdError] = useState('');
    const [clientIDError, setClientIDError] = useState('');
    const [copywriterError, setCopywriterError] = useState('');
    const [notesError, setNotesError] = useState('');
    const [inventoryError, setInventoryError] = useState('');

    const [touchedFields, setTouchedFields] = useState({});


    const [jobTypes, setJobTypes] = useState([]);
    const [users, setUsers] = useState([]);
    const [accountingStatuses, setAccountingStatuses] = useState([]);
    const [techniques, setTechniques] = useState([]);
    const [issues, setIssues] = useState([]);
    const [estimateStatuses, setEstimateStatuses] = useState([]);
    const [clients, setClients] = useState([]);

    const [activeTab, setActiveTab] = useState(() => {

        const requestedTab = location.state?.activeTab;
        if (requestedTab && allowedTabs.includes(requestedTab)) return requestedTab;
        return allowedTabs[0] || 'list-summary';
    });

    const [saving, setSaving] = useState(false);
    const [loadingClients, setLoadingClients] = useState(false);

    const [showSelection, setShowSelection] = useState(false);
    const gridApiRef = useRef(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);


    const [loadingJobTypes, setLoadingJobTypes] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [loadingAccountingStatuses, setLoadingAccountingStatuses] = useState(false);
    const [loadingTechniques, setLoadingTechniques] = useState(false);
    const [loadingIssues, setLoadingIssues] = useState(false);
    const [loadingEstimateStatuses, setLoadingEstimateStatuses] = useState(false);

    const [navigatingToJobList, setNavigatingToJobList] = useState(false);


    const dropdownsLoaded = useRef(false);


    const [reportsContext, setReportsContext] = useState(() => {
        if (location.state?.fromReports) {
            return {
                fromReports: true,
                reportFilters: location.state.reportFilters || {},
                selectedClientId: location.state.selectedClientId || [],
                reportType: location.state.reportType || '',
                rowData: location.state.rowData || []
            };
        }
        const stored = sessionStorage.getItem('reportsContext');
        return stored ? JSON.parse(stored) : null;
    });

    useEffect(() => {
        if (location.state?.fromReports) {
            const ctx = {
                fromReports: true,
                reportFilters: location.state.reportFilters || {},
                selectedClientId: location.state.selectedClientId || [],
                reportType: location.state.reportType || '',
                rowData: location.state.rowData || []
            };
            sessionStorage.setItem('reportsContext', JSON.stringify(ctx));
            setReportsContext(ctx);
        }
    }, [location.state]);


    const [navigationSource, setNavigationSource] = useState(() => {
        if (location.state?.source) return location.state.source;
        const stored = sessionStorage.getItem('jobDetailsSource');
        return stored || 'job-module';
    });

    const [isAllClientsFlow, setIsAllClientsFlow] = useState(() => {
        if (location.state?.isAllClients !== undefined) {
            return Boolean(location.state.isAllClients);
        }
        return sessionStorage.getItem('isAllClientsFlow') === 'true';
    });

    const [cameFromIntegration, setCameFromIntegration] = useState(() => {
        if (location.state?.fromIntegration !== undefined) {
            return Boolean(location.state.fromIntegration);
        }
        return sessionStorage.getItem('cameFromIntegration') === 'true';
    });


    useEffect(() => {
        if (jobId === 'new' && !canCreateOrCopyJob) {
            navigate('/job-module', { replace: true });
        }
    }, [jobId, canCreateOrCopyJob, navigate]);


    useEffect(() => {
        if (location.state?.source) {
            sessionStorage.setItem('jobDetailsSource', location.state.source);
            setNavigationSource(location.state.source);

            const fi = Boolean(location.state.fromIntegration);
            sessionStorage.setItem('cameFromIntegration', String(fi));
            setCameFromIntegration(fi);
        }
        if (location.state?.isAllClients !== undefined) {
            sessionStorage.setItem('isAllClientsFlow', String(Boolean(location.state.isAllClients)));
            setIsAllClientsFlow(Boolean(location.state.isAllClients));
        }
    }, [location.state]);


    const showClientSelectCrumb =
        navigationSource === 'dashboard' ||
        navigationSource === 'client-dashboard' ||
        isAllClientsFlow;

    const showClientNameCrumb =
        (navigationSource === 'dashboard' || navigationSource === 'client-dashboard') &&
        !isAllClientsFlow;

    const hideJobListsCrumb = showClientNameCrumb || navigationSource === 'list-upload' || navigationSource === 'reports';

    const handleJobListClick = (e) => {
        e.preventDefault();

        if (location.state?.fromReports) {
            navigate('/reports', {
                state: {
                    restoreReports: true,
                    reportFilters: location.state?.reportFilters || {},
                    selectedClientId: location.state?.selectedClientId || [],
                    selectedClients: location.state?.selectedClients || [],
                    selectedClientIds: location.state?.selectedClientIds || [],
                    reportType: location.state?.reportType || '',
                    rowData: location.state?.rowData || []
                }
            });
            return;
        }

        navigate('/job-module', {
            state: {
                clientId: jobData.clientID,
                clientName: clientName,
                source: navigationSource,
                isAllClients: isAllClientsFlow
            }
        });
    };

    useEffect(() => {
        if (location.state?.activeTab) {

            if (allowedTabs.includes(location.state.activeTab)) {
                setActiveTab(location.state.activeTab);
            } else {
                setActiveTab(allowedTabs[0] || 'list-summary');
            }
        }

    }, [location.state, canViewListSummaryTab, canViewCostSummaryTab, canViewFilesTab]);


    const mapJobData = (apiData) => ({
        jobName: apiData.jobName || '',
        jobId: apiData.jobCode || jobId || '',
        numericJobId: apiData.jobId || null,
        jobType: apiData.jobTypeId || null,
        mailDate: apiData.firstMailDate ? apiData.firstMailDate.split('T')[0] : '',
        productionManager: apiData.productionManagerID || null,
        copywriter: apiData.copywriter || '',
        accountingStatus: apiData.accountingStatusID || null,
        coordinator: apiData.coordinatorID || null,
        technique: apiData.techniqueID || null,
        issue: apiData.issueID || null,
        inventory: apiData.inventory || '',
        estimateStatus: apiData.estimateStatusID || null,
        notes: apiData.jobNote || apiData.notes || '',
        clientID: apiData.clientId || null
    });

    const fetchJobDetailsFromApi = async (code) => {
        if (!code || code === 'new') return;
        try {
            const data = await GetJobDetailsById(code);
            if (data) {
                setJobData(mapJobData(data));
            }
        } catch (error) {
            console.error('Failed to fetch job details:', error);
        }
    };


    const getJobTypeName = (id) => {
        if (!id) return 'Please select';
        const jobType = jobTypes.find(jt => jt.jobTypeID === id);
        return jobType ? jobType.jobTypeName : 'Please select';
    };

    const getProductionManagerName = (id) => {
        if (!id) return 'Please select';
        const user = users.find(u => u.userID === id);
        return user ? user.userName : 'Please select';
    };

    const getAccountingStatusName = (id) => {
        if (!id) return 'Please select';
        const status = accountingStatuses.find(as => as.accountingStatusID === id);
        return status ? status.accountingStatusName : 'Please select';
    };

    const getEstimateStatusName = (id) => {
        if (!id) return 'Please select';
        const status = estimateStatuses.find(es => es.estimateStatusID === id);
        return status ? status.estimateStatusName : 'Please select';
    };

    const getTechniqueName = (id) => {
        if (!id) return 'Please select';
        const technique = techniques.find(t => t.techniqueID === id);
        return technique ? technique.techniqueName : 'Please select';
    };

    const getIssueName = (id) => {
        if (!id) return 'Please select';
        const issue = issues.find(i => i.issueID === id);
        return issue ? issue.issueName : 'Please select';
    };

    const getClientName = (id) => {
        if (!id) return 'Please select';
        const client = clients.find(c => c.clientID === id);
        return client ? client.clientName : 'Please select';
    };

    const clientName = getClientName(jobData.clientID);

    const getClientCode = (id) => {
        if (!id) return 'Please select';
        const client = clients.find(c => c.clientID === id);
        return client ? client.clientCode : 'Please select';
    };


    const validateJobName = (value) => {
        if (!value || value.trim() === '') {
            return 'Job Name is required.';
        }
        const trimmedValue = value.trim();
        if (trimmedValue.length < 3) {
            return 'Job Name must be between 3 and 150 valid characters.';
        }
        if (trimmedValue.length > 150) {
            return 'Job Name must be between 3 and 150 valid characters.';
        }

        return '';
    };

    const validateMailDate = (dateValue) => {
        if (!dateValue || dateValue === '') {
            return 'Mail Date is required.';
        }
        try {
            const selectedDate = new Date(dateValue);
            selectedDate.setHours(0, 0, 0, 0);
            if (isNaN(selectedDate.getTime())) {
                return 'Mail Date must be a valid and recent date.';
            }
            const today = new Date();
            const threeSixtyFiveDaysAgo = new Date(today);
            threeSixtyFiveDaysAgo.setDate(today.getDate() - 365);
            threeSixtyFiveDaysAgo.setHours(0, 0, 0, 0);
            if (selectedDate < threeSixtyFiveDaysAgo) {
                return 'Mail Date must be a valid and recent date.';
            }
            return '';
        } catch (e) {
            return 'Mail Date must be a valid and recent date.';
        }
    };

    const validateJobId = (value) => {
        if (!value || value.trim() === '') {
            return 'Please enter a Job ID.';
        }
        if (value.includes(' ')) {
            return 'Job ID cannot contain spaces.';
        }
        const allowedCharsRegex = /^[a-zA-Z0-9-]+$/;
        if (!allowedCharsRegex.test(value)) {
            return 'Job ID can only contain letters, numbers, and hyphens (-). No other special characters allowed.';
        }
        if (value.length > 16) {
            return 'Job ID cannot exceed 16 characters.';
        }
        return '';
    };

    const validateClientID = (value) => {
        if (!value) return 'Please select a Client Name.';
        return '';
    };

    const validateJobType = (value) => {
        if (!value) return 'Please select a Job Type.';
        return '';
    };

    const validateProductionManager = (value) => {

        return '';

    };

    const validateAccountingStatus = (value) => {
        return '';
    };

    const validateEstimateStatus = (value) => {
        return '';
    };

    const validateNotes = (value) => {
        if (value && value.trim() !== '') {
            const trimmedValue = value.trim();
            if (trimmedValue.length < 3) return 'Notes must be at least 3 characters long.';
            if (trimmedValue.length > 2000) return 'Notes cannot exceed 2000 characters.';
        }
        return '';
    };

    const validateCopywriter = (value) => {
        if (value && value.trim() !== '') {
            const trimmedValue = value.trim();
            if (trimmedValue.length < 3) return 'Copywriter must be at least 3 characters long.';
            if (trimmedValue.length > 100) return 'Copywriter cannot exceed 100 characters.';
        }
        return '';
    };

    const validateInventory = (value) => {
        if (value && value.trim() !== '') {
            const trimmedValue = value.trim();
            if (trimmedValue.length < 3) return 'Inventory must be at least 3 characters long.';
            if (trimmedValue.length > 100) return 'Inventory cannot exceed 100 characters.';
        }
        return '';
    };

    // ========== HANDLE FIELD CHANGE WITH VALIDATION ==========
    const handleFieldChange = (field, value) => {
        setTouchedFields(prev => ({ ...prev, [field]: true }));

        let parsedValue = value;
        if (['jobType', 'productionManager', 'accountingStatus', 'estimateStatus', 'technique', 'issue', 'clientID'].includes(field)) {
            parsedValue = value ? (isNaN(parseInt(value)) ? value : parseInt(value)) : null;
        }

        if (field === 'mailDate') setMailDateError(validateMailDate(value));
        else if (field === 'jobName') setJobNameError(validateJobName(value));
        else if (field === 'jobType') setJobTypeError(validateJobType(parsedValue));
        else if (field === 'productionManager') setProductionManagerError(validateProductionManager(parsedValue));
        else if (field === 'accountingStatus') setAccountingStatusError(validateAccountingStatus(parsedValue));
        else if (field === 'estimateStatus') setEstimateStatusError(validateEstimateStatus(parsedValue));
        else if (field === 'jobId') setJobIdError(validateJobId(value));
        else if (field === 'clientID') setClientIDError(validateClientID(parsedValue));
        else if (field === 'copywriter') setCopywriterError(validateCopywriter(value));
        else if (field === 'notes') setNotesError(validateNotes(value));
        else if (field === 'inventory') setInventoryError(validateInventory(value));

        setJobData(prev => ({ ...prev, [field]: parsedValue }));
    };

    // ========== HANDLE FIELD BLUR ==========
    const handleFieldBlur = (field, value) => {
        setTouchedFields(prev => ({ ...prev, [field]: true }));

        let parsedValue = value;
        if (['jobType', 'productionManager', 'accountingStatus', 'estimateStatus', 'technique', 'issue', 'clientID'].includes(field)) {
            parsedValue = value ? (isNaN(parseInt(value)) ? value : parseInt(value)) : null;
        }

        if (field === 'mailDate') setMailDateError(validateMailDate(value));
        else if (field === 'jobName') setJobNameError(validateJobName(value));
        else if (field === 'jobType') setJobTypeError(validateJobType(parsedValue));
        else if (field === 'productionManager') setProductionManagerError(validateProductionManager(parsedValue));
        else if (field === 'accountingStatus') setAccountingStatusError(validateAccountingStatus(parsedValue));
        else if (field === 'estimateStatus') setEstimateStatusError(validateEstimateStatus(parsedValue));
        else if (field === 'jobId') setJobIdError(validateJobId(value));
        else if (field === 'clientID') setClientIDError(validateClientID(parsedValue));
        else if (field === 'notes') setNotesError(validateNotes(value));
        else if (field === 'copywriter') setCopywriterError(validateCopywriter(value));
        else if (field === 'inventory') setInventoryError(validateInventory(value));
    };


    const fetchJobTypes = async () => {
        try {
            setLoadingJobTypes(true);
            const response = await GetJobTypes();
            if (response && response.isSuccess && response.data) {
                setJobTypes(response.data);
            } else {
                setJobTypes([]);
            }
        } catch (error) {
            setJobTypes([]);
        } finally {
            setLoadingJobTypes(false);
        }
    };

    const fetchUsers = async () => {
        try {
            setLoadingUsers(true);
            const response = await GetAllUsersInternalRoles();
            if (response && Array.isArray(response)) {
                setUsers(response);
            } else if (response && response.data && Array.isArray(response.data)) {
                setUsers(response.data);
            } else if (response && response.isSuccess && response.data && Array.isArray(response.data)) {
                setUsers(response.data);
            } else {
                setUsers([]);
            }
        } catch (error) {
            setUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchClients = async () => {
        try {
            setLoadingClients(true);
            const response = await GetJobDetailsbyUserId();
            let clientList = [];
            if (Array.isArray(response)) {
                clientList = response.map(client => ({
                    clientID: client.clientID,
                    clientName: client.clientName,
                    clientCode: client.clientCode,
                    clientTypeName: client.clientTypeName,
                    isActive: client.isActive
                }));
            } else if (response && response.data && Array.isArray(response.data)) {
                clientList = response.data.map(client => ({
                    clientID: client.clientID,
                    clientName: client.clientName,
                    clientCode: client.clientCode,
                    clientTypeName: client.clientTypeName,
                    isActive: client.isActive
                }));
            }
            setClients(clientList);
        } catch (error) {
            setClients([]);
        } finally {
            setLoadingClients(false);
        }
    };

    const fetchAccountingStatuses = async () => {
        try {
            setLoadingAccountingStatuses(true);
            const response = await GetAccountingStatus();
            if (response && response.isSuccess && response.data && Array.isArray(response.data)) {
                setAccountingStatuses(response.data);
            } else if (response && response.data && Array.isArray(response.data)) {
                setAccountingStatuses(response.data);
            } else if (response && Array.isArray(response)) {
                setAccountingStatuses(response);
            } else {
                setAccountingStatuses([]);
            }
        } catch (error) {
            setAccountingStatuses([]);
        } finally {
            setLoadingAccountingStatuses(false);
        }
    };

    const fetchTechniques = async () => {
        try {
            setLoadingTechniques(true);
            const response = await GetJobTechniques();
            if (response && response.isSuccess && response.data && Array.isArray(response.data)) {
                setTechniques(response.data);
            } else if (response && response.data && Array.isArray(response.data)) {
                setTechniques(response.data);
            } else if (response && Array.isArray(response)) {
                setTechniques(response);
            } else {
                setTechniques([]);
            }
        } catch (error) {
            setTechniques([]);
        } finally {
            setLoadingTechniques(false);
        }
    };

    const fetchIssues = async () => {
        try {
            setLoadingIssues(true);
            const response = await GetJobIssues();
            if (response && response.isSuccess && response.data && Array.isArray(response.data)) {
                setIssues(response.data);
            } else if (response && response.data && Array.isArray(response.data)) {
                setIssues(response.data);
            } else if (response && Array.isArray(response)) {
                setIssues(response);
            } else {
                setIssues([]);
            }
        } catch (error) {
            setIssues([]);
        } finally {
            setLoadingIssues(false);
        }
    };

    const fetchEstimateStatuses = async () => {
        try {
            setLoadingEstimateStatuses(true);
            const response = await GetEstimateStatus();
            if (response && response.isSuccess && response.data && Array.isArray(response.data)) {
                setEstimateStatuses(response.data);
            } else if (response && response.data && Array.isArray(response.data)) {
                setEstimateStatuses(response.data);
            } else if (response && Array.isArray(response)) {
                setEstimateStatuses(response);
            } else {
                setEstimateStatuses([]);
            }
        } catch (error) {
            setEstimateStatuses([]);
        } finally {
            setLoadingEstimateStatuses(false);
        }
    };

    // ========== HANDLE DELETE JOB ==========
    const handleDeleteClick = () => {
        setIsMenuOpen(false);
        setShowDeleteConfirm(true);
    };

    const handleDeleteJob = async () => {
        try {
            setDeleting(true);
            const jobIdToDelete = jobData.numericJobId;
            if (!jobIdToDelete) return;
            await DeleteJobsById(jobIdToDelete);

            setShowDeleteConfirm(false);
            setNavigatingToJobList(true);

            const deletedJobId = jobData.jobId;
            setTimeout(() => {
                navigate('/job-module', {
                    state: {
                        successMessage: `Job ${deletedJobId} deleted successfully!`,
                        source: navigationSource,
                        isAllClients: isAllClientsFlow,
                        clientId: jobData.clientID
                    }
                });
            }, 100);
        } catch (error) {
            console.error('Error deleting job:', error);
            setSuccessMessage('Error deleting job. Please try again.');
            setMessageType('error');
            setDeleting(false);
            setShowDeleteConfirm(false);
        }
    };


    const handleSaveJob = async () => {
        try {
            const jobNameErr = validateJobName(jobData.jobName);
            const jobIdErr = isNewJob ? validateJobId(jobData.jobId) : '';
            const clientIDErr = validateClientID(jobData.clientID);
            const jobTypeErr = validateJobType(jobData.jobType);
            const dateErr = validateMailDate(jobData.mailDate);
            const productionManagerErr = validateProductionManager(jobData.productionManager);
            const copywriterErr = validateCopywriter(jobData.copywriter);
            const notesErr = validateNotes(jobData.notes);
            const inventoryErr = validateInventory(jobData.inventory);

            setJobNameError(jobNameErr);
            setJobIdError(jobIdErr);
            setClientIDError(clientIDErr);
            setJobTypeError(jobTypeErr);
            setMailDateError(dateErr);
            setProductionManagerError(productionManagerErr);
            setCopywriterError(copywriterErr);
            setNotesError(notesErr);
            setInventoryError(inventoryErr);

            setTouchedFields(prev => ({
                ...prev,
                jobName: true,
                jobId: true,
                clientID: true,
                jobType: true,
                mailDate: true,
                productionManager: true,
                copywriter: true,
                notes: true,
                inventory: true,
            }));

            const hasErrors = Boolean(
                jobNameErr || jobIdErr || clientIDErr || jobTypeErr ||
                dateErr || copywriterErr || notesErr || inventoryErr
            );

            if (hasErrors) {
                return;
            }

            setSaving(true);

            let formattedFirstMailDate = null;
            if (jobData.mailDate) {
                try {
                    formattedFirstMailDate = `${jobData.mailDate}T00:00:00.000Z`;
                } catch (e) {
                    console.error('Date parsing error:', e);
                }
            }

            const jobPayload = {
                jobName: jobData.jobName?.trim() || '',
                jobCode: jobData.jobId?.trim(),
                jobTypeID: jobData.jobType,
                firstMailDate: formattedFirstMailDate,
                clientID: jobData.clientID,
                productionManagerID: jobData.productionManager,
                copywriter: jobData.copywriter || '',
                accountingStatusID: jobData.accountingStatus || null,
                estimateStatusID: jobData.estimateStatus || null,
                techniqueID: jobData.technique,
                issueID: jobData.issue,
                inventory: jobData.inventory || '',
                notes: jobData.notes || '',
                createdByUserId: 0
            };

            const response = await CreateNewJobs(jobPayload);

            if (response) {
                if (response?.errors) {
                    const firstErrorField = Object.keys(response.errors)[0];
                    if (firstErrorField) {
                        const errorValue = response.errors[firstErrorField];
                        const errorMessage = Array.isArray(errorValue) ? errorValue[0] : errorValue;
                        setSuccessMessage(errorMessage);
                        setMessageType('error');
                        return;
                    }
                }

                let newJobData = response.data || response;
                let newJobId = newJobData.jobCode || newJobData.jobId || response.jobId;
                let numericJobId = newJobData.jobId || newJobData.numericJobId;

                setIsCopyMode(false);
                setOriginalJobId('');
                setIsNewJob(false);

                if (newJobId && newJobId !== 'NEW' && newJobId !== '') {
                    dropdownsLoaded.current = true;

                    try {
                        const jobDetailsResponse = await GetJobDetailsbyUserId(newJobId);
                        let completeJobData = newJobData;
                        if (jobDetailsResponse && jobDetailsResponse.data) {
                            completeJobData = jobDetailsResponse.data;
                        } else if (jobDetailsResponse && Array.isArray(jobDetailsResponse)) {
                            const foundJob = jobDetailsResponse.find(
                                job => job.jobCode === newJobId || job.jobId === numericJobId
                            );
                            if (foundJob) completeJobData = foundJob;
                        }

                        navigate(`/job-details/${newJobId}`, {
                            state: {
                                jobData: completeJobData,
                                successMessage: 'Job created successfully!',
                                isNewlyCreated: true,
                                source: navigationSource,
                                isAllClients: isAllClientsFlow
                            }
                        });
                    } catch (fetchError) {
                        console.error('Error fetching job details after creation:', fetchError);
                        navigate(`/job-details/${newJobId}`, {
                            state: {
                                jobData: newJobData,
                                successMessage: 'Job created successfully!',
                                isNewlyCreated: true,
                                source: navigationSource,
                                isAllClients: isAllClientsFlow
                            }
                        });
                    }
                } else {
                    console.error('No valid job ID received in response');
                    window.location.reload();
                }
            } else {
                setSuccessMessage('Failed to save job: No response from server');
                setMessageType('error');
            }
        } catch (error) {
            console.error('Error saving job:', error);
            if (error.response?.status === 201 || error.response?.status === 200) {
                const responseData = error.response.data;
                const newJobId = responseData?.jobCode || responseData?.jobId;

                if (newJobId && newJobId !== 'NEW' && newJobId !== '') {
                    dropdownsLoaded.current = true;
                    setIsCopyMode(false);
                    setOriginalJobId('');
                    setIsNewJob(false);
                    navigate(`/job-details/${newJobId}`, {
                        state: {
                            jobData: responseData,
                            successMessage: 'Job created successfully!',
                            isNewlyCreated: true,
                            source: navigationSource,
                            isAllClients: isAllClientsFlow
                        }
                    });
                } else {
                    window.location.reload();
                }
            } else {
                setSuccessMessage('Error saving job. Please try again.');
                setMessageType('error');
            }
        } finally {
            setSaving(false);
        }
    };

    // ========== HANDLE UPDATE JOB ==========
    const handleUpdateJob = async () => {
        if (isNewJob) return;

        try {
            const jobNameErr = validateJobName(jobData.jobName);
            const clientIDErr = validateClientID(jobData.clientID);
            const jobTypeErr = validateJobType(jobData.jobType);
            const dateErr = validateMailDate(jobData.mailDate);
            const copywriterErr = validateCopywriter(jobData.copywriter);
            const notesErr = validateNotes(jobData.notes);
            const inventoryErr = validateInventory(jobData.inventory);

            setJobNameError(jobNameErr);
            setClientIDError(clientIDErr);
            setJobTypeError(jobTypeErr);
            setMailDateError(dateErr);
            setCopywriterError(copywriterErr);
            setNotesError(notesErr);
            setInventoryError(inventoryErr);

            setTouchedFields(prev => ({
                ...prev,
                jobName: true,
                clientID: true,
                jobType: true,
                mailDate: true,
                productionManager: true,
                copywriter: true,
                notes: true,
                inventory: true,
            }));

            if (!jobData.numericJobId) {
                setSuccessMessage('Cannot update job: No numeric job ID found');
                setMessageType('error');
                return;
            }

            const hasErrors = Boolean(
                jobNameErr || clientIDErr || jobTypeErr ||
                dateErr || copywriterErr || notesErr || inventoryErr
            );

            if (hasErrors) {
                return;
            }

            setSaving(true);

            let formattedFirstMailDate = null;
            if (jobData.mailDate) {
                try {
                    formattedFirstMailDate = `${jobData.mailDate}T00:00:00.000Z`;
                } catch (e) {
                    console.error('Date parsing error:', e);
                }
            }

            const updatePayload = {
                jobId: jobData.numericJobId,
                jobName: jobData.jobName?.trim() || '',
                jobCode: jobData.jobId,
                jobTypeID: jobData.jobType,
                firstMailDate: formattedFirstMailDate,
                clientID: jobData.clientID,
                productionManagerID: jobData.productionManager,
                copywriter: jobData.copywriter || '',
                accountingStatusID: jobData.accountingStatus || null,
                estimateStatusID: jobData.estimateStatus || null,
                techniqueID: jobData.technique,
                issueID: jobData.issue,
                inventory: jobData.inventory || '',
                notes: jobData.notes || '',
                createdByUserId: 0
            };

            const response = await UpdateJobsById(jobData.numericJobId, updatePayload);

            let updateSucceeded = false;

            if (response) {
                if (response?.errors) {
                    const firstErrorField = Object.keys(response.errors)[0];
                    if (firstErrorField) {
                        const errorValue = response.errors[firstErrorField];
                        const errorMessage = Array.isArray(errorValue) ? errorValue[0] : errorValue;
                        setSuccessMessage(errorMessage);
                        setMessageType('error');
                        return;
                    }
                }

                updateSucceeded = true;

                if (response.data) {
                    const updatedData = response.data;
                    setJobData(prev => ({
                        ...prev,
                        jobName: updatedData.jobName || prev.jobName,
                        jobType: updatedData.jobTypeID || prev.jobType,
                        mailDate: updatedData.firstMailDate ? updatedData.firstMailDate.split('T')[0] : prev.mailDate,
                        productionManager: updatedData.productionManagerID || prev.productionManager,
                        copywriter: updatedData.copywriter || prev.copywriter,
                        accountingStatus: updatedData.accountingStatusID || prev.accountingStatus,
                        estimateStatus: updatedData.estimateStatusID || prev.estimateStatus,
                        technique: updatedData.techniqueID || prev.technique,
                        issue: updatedData.issueID || prev.issue,
                        inventory: updatedData.inventory || prev.inventory,
                        notes: updatedData.jobNote || updatedData.notes || prev.notes,
                        clientID: updatedData.clientID || prev.clientID
                    }));
                }
            }

            setSaving(false);

            if (updateSucceeded) {
                setTimeout(() => {
                    setSuccessMessage('Job updated successfully!');
                }, 150);
            }
            return;
        } catch (error) {
            console.error('Error updating job:', error);
            if (error.response?.status === 200 || error.response?.status === 204) {
                setSaving(false);
                setTimeout(() => {
                    setSuccessMessage('Job updated successfully!');
                }, 150);
            } else {
                setSuccessMessage('Error updating job. Please try again.');
                setMessageType('error');
                setSaving(false);
            }
        }
    };

    useEffect(() => {
        const init = async () => {

            if (!canViewAnything) return;

            setLoading(true);

            if (!dropdownsLoaded.current) {
                await Promise.all([
                    fetchJobTypes(),
                    fetchUsers(),
                    fetchAccountingStatuses(),
                    fetchTechniques(),
                    fetchIssues(),
                    fetchEstimateStatuses(),
                    fetchClients(),
                ]);
                dropdownsLoaded.current = true;
            }

            if (jobId === 'new') {
                setIsNewJob(true);
                setLoading(false);
                return;
            }

            setIsNewJob(false);

            if (location.state?.isNewlyCreated && location.state?.jobData) {
                setJobData(mapJobData(location.state.jobData));
                setLoading(false);

                if (location.state?.successMessage) {
                    setTimeout(() => {
                        setSuccessMessage(location.state.successMessage);
                        window.history.replaceState({}, document.title);
                    }, 150);
                }
                return;
            }

            if (location.state?.jobData) {
                setJobData(mapJobData(location.state.jobData));
                setLoading(false);
                fetchJobDetailsFromApi(jobId);
                return;
            }

            await fetchJobDetailsFromApi(jobId);
            setLoading(false);
        };

        init();

    }, [jobId]);

    useEffect(() => {
        if (jobId !== 'new') {
            setIsCopyMode(false);
            setOriginalJobId('');
        }
    }, [jobId]);

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const handleCancel = () => {
        navigate('/job-module', {
            state: {
                clientId: jobData.clientID,
                source: navigationSource,
                isAllClients: isAllClientsFlow
            }
        });
    };

    const toggleJobInfo = () => {
        setIsJobInfoCollapsed(!isJobInfoCollapsed);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.job-details-menu-container')) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleTabClick = (tabId) => {
        // Only switch to tabs this user is actually permitted to view
        if (!allowedTabs.includes(tabId)) return;
        setActiveTab(tabId);
    };

    // RBAC: render nothing while redirecting a fully-blocked user
    if (!canViewAnything) {
        return <AisScreenLoader />;
    }

    if (loading || saving || navigatingToJobList || deleting) {
        return <AisScreenLoader />;
    }

    const getPMReviewingStatusId = () => {
        const pmReviewingStatus = accountingStatuses.find(
            status => status.accountingStatusName === 'PM Reviewing'
        );
        return pmReviewingStatus ? pmReviewingStatus.accountingStatusID : null;
    };

    const handleCreateNewJob = () => {
        if (!canCreateOrCopyJob) return;
        setIsMenuOpen(false);
        const pmReviewingId = getPMReviewingStatusId();

        setJobData({
            jobName: '',
            jobId: '',
            numericJobId: null,
            jobType: null,
            mailDate: '',
            productionManager: null,
            copywriter: '',
            accountingStatus: pmReviewingId,
            coordinator: null,
            technique: null,
            issue: null,
            inventory: '',
            estimateStatus: null,
            notes: '',
            clientID: null
        });

        setMailDateError('');
        setJobNameError('');
        setJobTypeError('');
        setProductionManagerError('');
        setAccountingStatusError('');
        setEstimateStatusError('');
        setJobIdError('');
        setClientIDError('');
        setCopywriterError('');
        setNotesError('');
        setInventoryError('');
        setTouchedFields({});

        setIsNewJob(true);
        setIsCopyMode(false);
        setOriginalJobId('');

        navigate('/job-details/new', {
            state: {
                source: navigationSource,
                isAllClients: isAllClientsFlow
            }
        });
    };

    // ========== COPY JOB HANDLER ==========
    const handleCopyJob = () => {
        if (!canCreateOrCopyJob) return; // safety guard
        setIsMenuOpen(false);
        const pmReviewingId = getPMReviewingStatusId();
        setOriginalJobId(jobData.jobId);

        setJobData({
            jobName: '',
            jobId: '',
            numericJobId: null,
            jobType: jobData.jobType || null,
            mailDate: jobData.mailDate || new Date().toLocaleDateString('en-US'),
            productionManager: jobData.productionManager || null,
            copywriter: jobData.copywriter || '',
            accountingStatus: pmReviewingId,
            coordinator: jobData.coordinator || null,
            technique: jobData.technique || null,
            issue: jobData.issue || null,
            inventory: jobData.inventory || '',
            estimateStatus: null,
            notes: '',
            clientID: jobData.clientID || null
        });

        setIsCopyMode(true);
        setIsNewJob(true);

        navigate('/job-details/new', {
            state: {
                source: navigationSource,
                isAllClients: isAllClientsFlow
            }
        });
    };




    return (
        <div className='job-details-main-container'>

            {successMessage && (
                <SuccessMessage
                    message={successMessage}
                    type={messageType}
                    onClose={() => {
                        setSuccessMessage('');
                        setMessageType('success');
                    }}
                />
            )}

            {/* CONDITIONAL BREADCRUMB */}
            <div className="job-details-breadcrumb">
                <nav className="breadcrumb-nav">
                    <ol className="breadcrumb-list">

                        {location.state?.fromReports && (
                            <>
                                <li className="breadcrumb-item">
                                    <a
                                        href="/reports"
                                        className="breadcrumb-link"
                                        onClick={(e) => {
                                            e.preventDefault();

                                            navigate('/reports', {
                                                state: {
                                                    restoreReports: true,
                                                    reportFilters: location.state?.reportFilters || {},
                                                    selectedClientId: location.state?.selectedClientId || [],
                                                    selectedClients: location.state?.selectedClients || [],
                                                    selectedClientIds: location.state?.selectedClientIds || [],
                                                    reportType: location.state?.reportType || '',
                                                    rowData: location.state?.rowData || []
                                                }
                                            });
                                        }}
                                    >
                                        Reports
                                    </a>
                                </li>
                                <li className="breadcrumb-separator">/</li>
                            </>
                        )}


                        {showClientSelectCrumb && (
                            <>
                                <li className="breadcrumb-item">
                                    <Icon
                                        name="user-icon"
                                        size={14}
                                        alt="user-icon"
                                        className="breadcrumb-icon"
                                    />
                                    <a
                                        href="/dashboard"
                                        className="breadcrumb-link"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            sessionStorage.removeItem('jobDetailsSource');
                                            sessionStorage.removeItem('isAllClientsFlow');
                                            sessionStorage.removeItem('cameFromIntegration');
                                            navigate('/dashboard');
                                        }}
                                    >
                                        Client Select
                                    </a>
                                </li>
                                <li className="breadcrumb-separator">/</li>
                            </>
                        )}


                        {showClientNameCrumb && (
                            <>
                                <li className="breadcrumb-item">
                                    <a
                                        href="/client-dashboard"
                                        className="breadcrumb-link"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate('/client-dashboard', {
                                                state: {
                                                    clientId: jobData.clientID,
                                                    source: 'client-dashboard',
                                                    isAllClients: false
                                                }
                                            });
                                        }}
                                    >
                                        Client: {getClientCode(jobData.clientID)}
                                    </a>
                                </li>
                                <li className="breadcrumb-separator">/</li>
                            </>
                        )}


                        {cameFromIntegration && navigationSource === 'client-dashboard' && (
                            <>
                                <li className="breadcrumb-item">
                                    <a
                                        href="#"
                                        className="breadcrumb-link"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate('/talliesSection', {
                                                state: {
                                                    activeTab: 'lists',
                                                    clientId: jobData.clientID,
                                                    clientName: clientName
                                                }
                                            });
                                        }}
                                    >
                                        Integration
                                    </a>
                                </li>
                                <li className="breadcrumb-separator">/</li>
                            </>
                        )}


                        {navigationSource === 'list-upload' && (
                            <>
                                <li className="breadcrumb-item">
                                    <a
                                        href="#"
                                        className="breadcrumb-link"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            sessionStorage.removeItem('jobDetailsSource');
                                            sessionStorage.removeItem('isAllClientsFlow');
                                            navigate('/talliesSection', {
                                                state: { activeTab: 'lists' }
                                            });
                                        }}
                                    >
                                        Integration
                                    </a>
                                </li>
                                <li className="breadcrumb-separator">/</li>
                            </>
                        )}


                        {!hideJobListsCrumb && (
                            <>
                                <li className="breadcrumb-item d-flex align-items-center">
                                    <span style={{ paddingRight: "4px" }}>
                                        <Icon name="mail-icon" size={14} alt="mail-icon" />
                                    </span>
                                    <a
                                        href="/job-module"
                                        className="breadcrumb-link"
                                        onClick={handleJobListClick}
                                    >
                                        Job Lists
                                    </a>
                                </li>

                                <li className="breadcrumb-separator">/</li>
                            </>
                        )}



                        <li className="breadcrumb-item active">
                            {window.location.pathname.includes('/new')
                                ? (isCopyMode ? `Copy of ${originalJobId}` : 'Create New Job')
                                : `${jobData.jobId} Job Details`}
                        </li>

                    </ol>
                </nav>
            </div>

            <div className="job-details-header">
                <h1 className="job-details-title">
                    {window.location.pathname.includes('/new')
                        ? (isCopyMode ? `Copy of ${originalJobId || 'Job'}` : 'Create New Job ')
                        : `${jobData.jobId} - ${jobData.jobName || 'Job Details'}`
                    }
                </h1>

                {!window.location.pathname.includes('/new') && canManageJob && (
                    <div className="job-details-menu-container">
                        <button className="job-details-menu-button" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                            <span className="menu-dot"></span>
                            <span className="menu-dot"></span>
                            <span className="menu-dot"></span>
                        </button>

                        {isMenuOpen && (
                            <div className="job-details-menu-dropdown">
                                {canCreateOrCopyJob && (
                                    <button
                                        className="menu-item create-job"
                                        onClick={handleCreateNewJob}
                                        disabled={saving}
                                    >
                                        Create New Job
                                    </button>
                                )}
                                {canCreateOrCopyJob && (
                                    <button
                                        className='menu-item create-job'
                                        style={{ borderBottom: "1px solid #eee" }}
                                        onClick={handleCopyJob}
                                    >
                                        Copy Job
                                    </button>
                                )}
                                {canDeleteJob && (
                                    <button className="menu-item delete-job" onClick={handleDeleteClick}>
                                        Delete Job
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>


            {canViewJobInfo && (
                <div className={`job-information-container ${isJobInfoCollapsed ? 'collapsed' : ''}`}>

                    <div className='job-information-header'>
                        <span>Job Information</span>
                        <button
                            className="job-info-toggle-btn"
                            onClick={toggleJobInfo}
                            aria-label={isJobInfoCollapsed ? "Expand job information" : "Collapse job information"}
                        >
                            {isJobInfoCollapsed ?
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                </svg> :
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="18 15 12 9 6 15"></polyline>
                                </svg>
                            }
                        </button>
                    </div>

                    {!isJobInfoCollapsed && (
                        <div className="job-details-grid-row-full">

                            <div className="job-details-inner-grid">

                                {/* Job Name */}
                                <div>
                                    <label className="job-details-label">
                                        Job Name {canEditJobInfo && <span style={{ color: '#d32f2f' }}>*</span>}
                                    </label>
                                    <input
                                        type="text"
                                        value={jobData.jobName}
                                        onChange={(e) => handleFieldChange('jobName', e.target.value)}
                                        onBlur={(e) => handleFieldBlur('jobName', e.target.value)}
                                        className={`job-details-input ${jobNameError && touchedFields.jobName ? 'input-error' : ''}`}
                                        placeholder="Enter job name"
                                        maxLength={150}
                                        disabled={!canEditJobInfo}
                                    />
                                    {jobNameError && touchedFields.jobName && (
                                        <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            {jobNameError}
                                        </div>
                                    )}
                                </div>

                                {/* Job ID */}
                                <div>
                                    <label className="job-details-label">
                                        Job ID {canEditJobInfo && <span style={{ color: '#d32f2f' }}>*</span>}
                                    </label>
                                    <input
                                        type="text"
                                        value={jobData.jobId}
                                        onChange={(e) => handleFieldChange('jobId', e.target.value)}
                                        onBlur={(e) => handleFieldBlur('jobId', e.target.value)}
                                        className={`job-details-input ${jobIdError && touchedFields.jobId ? 'input-error' : ''}`}
                                        placeholder="Enter Job ID"
                                        maxLength={16}
                                        disabled={!window.location.pathname.includes('/new') || !canEditJobInfo}
                                    />
                                    {jobIdError && touchedFields.jobId && (
                                        <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            {jobIdError}
                                        </div>
                                    )}
                                </div>

                                {/* Client Name */}
                                <div>
                                    <label className="job-details-label">
                                        Client Name {canEditJobInfo && <span style={{ color: '#d32f2f' }}>*</span>}
                                    </label>
                                    <select
                                        value={jobData.clientID || ''}
                                        onChange={(e) => handleFieldChange('clientID', e.target.value)}
                                        onBlur={(e) => handleFieldBlur('clientID', e.target.value)}
                                        className={`job-details-select ${clientIDError && touchedFields.clientID ? 'input-error' : ''}`}
                                        disabled={loadingClients || !canEditJobInfo}
                                    >
                                        <option value="">Please select</option>
                                        {clients.length > 0 ? (
                                            clients.map((client) => (
                                                <option key={client.clientID} value={client.clientID}>
                                                    {client.clientName}
                                                </option>
                                            ))
                                        ) : (
                                            !loadingClients && <option disabled>No clients available</option>
                                        )}
                                    </select>
                                    {clientIDError && touchedFields.clientID && (
                                        <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            {clientIDError}
                                        </div>
                                    )}
                                    {loadingClients && (
                                        <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                                            Loading clients...
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="job-details-grid-row-full">
                                <div className="job-details-inner-grid">

                                    {/* Job Type */}
                                    <div>
                                        <label className="job-details-label">
                                            Job Type {canEditJobInfo && <span style={{ color: '#d32f2f' }}>*</span>}
                                        </label>
                                        <select
                                            value={jobData.jobType || ''}
                                            onChange={(e) => handleFieldChange('jobType', e.target.value)}
                                            onBlur={(e) => handleFieldBlur('jobType', e.target.value)}
                                            className={`job-details-select ${jobTypeError && touchedFields.jobType ? 'input-error' : ''}`}
                                            disabled={loadingJobTypes || !canEditJobInfo}
                                        >
                                            <option value="">Please select</option>
                                            {jobTypes.map((jobType) => (
                                                <option key={jobType.jobTypeID} value={jobType.jobTypeID}>
                                                    {jobType.jobTypeName}
                                                </option>
                                            ))}
                                        </select>
                                        {jobTypeError && touchedFields.jobType && (
                                            <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                {jobTypeError}
                                            </div>
                                        )}
                                        {loadingJobTypes && (
                                            <div className="job-types-loading">Loading job types...</div>
                                        )}
                                    </div>

                                    {/* Mail Date */}
                                    <div>
                                        <label className="job-details-label">
                                            Mail Date {canEditJobInfo && <span style={{ color: '#d32f2f' }}>*</span>}
                                        </label>
                                        <div className={`date-wrapper ${jobData.mailDate ? 'has-value' : ''}`}>
                                            <input
                                                type="date"
                                                value={jobData.mailDate || ''}
                                                onChange={(e) => handleFieldChange('mailDate', e.target.value)}
                                                onBlur={(e) => handleFieldBlur('mailDate', e.target.value)}
                                                className={`job-details-input ${mailDateError && touchedFields.mailDate ? 'input-error' : ''}`}
                                                disabled={!canEditJobInfo}
                                            />
                                        </div>
                                        {mailDateError && touchedFields.mailDate && (
                                            <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                {mailDateError}
                                            </div>
                                        )}
                                    </div>

                                    {/* Production Manager */}
                                    <div>
                                        <label className="job-details-label">

                                            Production Manager

                                        </label>
                                        <select
                                            value={jobData.productionManager || ''}
                                            onChange={(e) => handleFieldChange('productionManager', e.target.value)}
                                            onBlur={(e) => handleFieldBlur('productionManager', e.target.value)}
                                            className={`job-details-select ${productionManagerError && touchedFields.productionManager ? 'input-error' : ''}`}
                                            disabled={loadingUsers || !canEditJobInfo}
                                        >
                                            <option value="">Please select</option>
                                            {users
                                                .sort((a, b) => a.userName.localeCompare(b.userName))
                                                .map((user) => (
                                                    <option key={`pm-${user.userID}`} value={user.userID}>
                                                        {user.userName}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                        {productionManagerError && touchedFields.productionManager && (
                                            <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                {productionManagerError}
                                            </div>
                                        )}
                                        {loadingUsers && (
                                            <div className="users-loading">Loading users...</div>
                                        )}
                                    </div>
                                </div>
                            </div>


                            <div className="job-details-grid-row-full">
                                <div className="job-details-inner-grid">

                                    {/* Copywriter */}
                                    <div>
                                        <label className="job-details-label">Copywriter</label>
                                        <input
                                            type="text"
                                            value={jobData.copywriter}
                                            onChange={(e) => handleFieldChange('copywriter', e.target.value)}
                                            onBlur={(e) => handleFieldBlur('copywriter', e.target.value)}
                                            className={`job-details-input ${copywriterError && touchedFields.copywriter ? 'input-error' : ''}`}
                                            placeholder="Enter copywriter name"
                                            maxLength={100}
                                            disabled={!canEditJobInfo}
                                        />
                                        {copywriterError && touchedFields.copywriter && (
                                            <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                {copywriterError}
                                            </div>
                                        )}
                                    </div>


                                    <div>
                                        <label className="job-details-label">
                                            Accounting Status
                                        </label>
                                        <select
                                            value={jobData.accountingStatus || ''}
                                            onChange={(e) => handleFieldChange('accountingStatus', e.target.value)}
                                            onBlur={(e) => handleFieldBlur('accountingStatus', e.target.value)}
                                            className={`job-details-select ${accountingStatusError && touchedFields.accountingStatus ? 'input-error' : ''}`}
                                            disabled={loadingAccountingStatuses || !canEditJobInfo}
                                        >

                                            {accountingStatuses.map((status) => (
                                                <option key={status.accountingStatusID} value={status.accountingStatusID}>
                                                    {status.accountingStatusName}
                                                </option>
                                            ))}
                                        </select>
                                        {accountingStatusError && touchedFields.accountingStatus && (
                                            <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                {accountingStatusError}
                                            </div>
                                        )}
                                        {loadingAccountingStatuses && (
                                            <div className="accounting-status-loading">Loading accounting statuses...</div>
                                        )}
                                    </div>


                                    <div>
                                        <label className="job-details-label">
                                            Estimate Status
                                        </label>
                                        <div className="job-details-estimate-container">
                                            <select
                                                value={jobData.estimateStatus || ''}
                                                onChange={(e) => handleFieldChange('estimateStatus', e.target.value)}
                                                onBlur={(e) => handleFieldBlur('estimateStatus', e.target.value)}
                                                className={`job-details-select ${estimateStatusError && touchedFields.estimateStatus ? 'input-error' : ''}`}
                                                disabled={loadingEstimateStatuses || !canEditJobInfo}
                                            >
                                                <option value="">Please select</option>
                                                {estimateStatuses.map((status) => (
                                                    <option key={status.estimateStatusID} value={status.estimateStatusID}>
                                                        {status.estimateStatusName}
                                                    </option>
                                                ))}
                                            </select>
                                            {estimateStatusError && touchedFields.estimateStatus && (
                                                <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    {estimateStatusError}
                                                </div>
                                            )}
                                            {loadingEstimateStatuses && (
                                                <div className="estimate-status-loading">Loading estimate statuses...</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>


                            <div className="job-details-grid-row-full">
                                <div className="job-details-inner-grid">

                                    {/* Technique */}
                                    <div>
                                        <label className="job-details-label">Technique</label>
                                        <select
                                            value={jobData.technique || ''}
                                            onChange={(e) => handleFieldChange('technique', e.target.value)}
                                            onBlur={(e) => handleFieldBlur('technique', e.target.value)}
                                            className="job-details-select"
                                            disabled={loadingTechniques || !canEditJobInfo}
                                        >
                                            <option value="">Please select</option>
                                            {techniques.map((technique) => (
                                                <option key={technique.techniqueID} value={technique.techniqueID}>
                                                    {technique.techniqueName}
                                                </option>
                                            ))}
                                        </select>
                                        {loadingTechniques && (
                                            <div className="techniques-loading">Loading techniques...</div>
                                        )}
                                    </div>


                                    <div>
                                        <label className="job-details-label">Issue</label>
                                        <select
                                            value={jobData.issue || ''}
                                            onChange={(e) => handleFieldChange('issue', e.target.value)}
                                            onBlur={(e) => handleFieldBlur('issue', e.target.value)}
                                            className="job-details-select"
                                            disabled={loadingIssues || !canEditJobInfo}
                                        >
                                            <option value="">Please select</option>
                                            {issues.map((issue) => (
                                                <option key={issue.issueID} value={issue.issueID}>
                                                    {issue.issueName}
                                                </option>
                                            ))}
                                        </select>
                                        {loadingIssues && (
                                            <div className="issues-loading">Loading issues...</div>
                                        )}
                                    </div>


                                    <div>
                                        <label className="job-details-label">Inventory</label>
                                        <input
                                            type="text"
                                            value={jobData.inventory}
                                            onChange={(e) => handleFieldChange('inventory', e.target.value)}
                                            onBlur={(e) => handleFieldBlur('inventory', e.target.value)}
                                            className={`job-details-input ${inventoryError && touchedFields.inventory ? 'input-error' : ''}`}
                                            placeholder="Enter inventory status"
                                            maxLength={100}
                                            disabled={!canEditJobInfo}
                                        />
                                        {inventoryError && touchedFields.inventory && (
                                            <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                {inventoryError}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Row 5: Notes Section */}
                            <div className="job-details-estimate-section">
                                <div className="job-details-notes-section">
                                    <label className="job-details-label">Notes</label>
                                    <textarea
                                        value={jobData.notes}
                                        onChange={(e) => handleFieldChange('notes', e.target.value)}
                                        onBlur={(e) => handleFieldBlur('notes', e.target.value)}
                                        className={`job-details-textarea ${notesError && touchedFields.notes ? 'input-error' : ''}`}
                                        minLength={3}
                                        maxLength={2000}
                                        placeholder="Add any additional notes or comments..."
                                        rows={4}
                                        disabled={!canEditJobInfo}
                                    />
                                    {notesError && touchedFields.notes && (
                                        <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            {notesError}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {!isListAgentUser && (
                                <div className="job-details-button-row">
                                    <div className="button-container-jobs">
                                        <button
                                            className="cancel-btn"
                                            onClick={handleCancel}
                                            disabled={saving}
                                        >
                                            Cancel
                                        </button>
                                        {canEditJobInfo && (
                                            <button
                                                className="save-changes-btn"
                                                onClick={window.location.pathname.includes('/new') ? handleSaveJob : handleUpdateJob}
                                                disabled={saving}
                                            >
                                                {saving ? 'Saving...' : (window.location.pathname.includes('/new') ? 'Create Job' : 'Save Changes')}
                                            </button>
                                        )}
                                    </div>
                                </div>

                            )}

                        </div>
                    )}
                </div>
            )}


            {!window.location.pathname.includes('/new') && jobData.numericJobId && allowedTabs.length > 0 && (
                <div className='list-summary-container'>
                    <div className="tabs-interface">
                        <div className="tabs-header">
                            <div className="tabs-navigation">


                                {canViewListSummaryTab && (
                                    <button
                                        className={`tab-button ${activeTab === 'list-summary' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('list-summary')}
                                        data-tab="list-summary"
                                    >
                                        <Icon name="list-summary-icon" size={16} alt="list-summary-icon" />
                                        List Summary
                                    </button>
                                )}

                                {/* ✅ Cost Summary Tab — Jobs.CostSummary.fullTab */}
                                {canViewCostSummaryTab && (
                                    <button
                                        className={`tab-button ${activeTab === 'cost-summary' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('cost-summary')}
                                        data-tab="cost-summary"
                                    >
                                        <Icon name="dollar-icon" size={16} alt="dollar-icon" />
                                        Cost Summary
                                    </button>
                                )}


                                {canViewFilesTab && (
                                    <button
                                        className={`tab-button ${activeTab === 'files' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('files')}
                                        data-tab="files"
                                    >
                                        <Icon name="files-icon" size={16} alt="files-icon" />
                                        Files
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="tabs-content">
                            {activeTab === 'list-summary' && canViewListSummaryTab && (
                                <div className="tab-pane active" id="list-summary">
                                    <ListSummaryTab
                                        key={`list-summary-${activeTab}`}
                                        jobId={jobData.numericJobId}
                                        jobCode={jobData.jobId}
                                        clientID={jobData.clientID}
                                        clientName={clientName}
                                        navigationSource={navigationSource}
                                        isAllClientsFlow={isAllClientsFlow}
                                        fromReports={reportsContext?.fromReports}
                                        reportFilters={reportsContext?.reportFilters}
                                        selectedClientId={reportsContext?.selectedClientId}
                                        reportType={reportsContext?.reportType}
                                        rowData={reportsContext?.rowData}
                                    />
                                </div>
                            )}
                            {activeTab === 'cost-summary' && canViewCostSummaryTab && (
                                <div className="tab-pane active" id="cost-summary">
                                    <CostSummary
                                        key={`cost-summary-${activeTab}`}
                                        jobId={jobData.numericJobId}
                                        jobCode={jobData.jobId}
                                        clientName={getClientCode(jobData.clientID)}
                                        clientId={jobData.clientID}   // ✅ ADD THIS
                                        viewOnly={!canEditCostSummaryTab}   // ✅ view-only unless canEdit rule allows it
                                    />
                                </div>
                            )}
                            {activeTab === 'files' && canViewFilesTab && (
                                <div className="tab-pane active" id="files">
                                    <FilesTab jobId={jobData.jobId} numericJobId={jobData.numericJobId} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="delete-confirmation-overlay">
                    <div className="delete-confirmation-modal">
                        <h3>Confirm Delete</h3>
                        <p>Are you sure you want to delete job <strong>{jobData.jobName}</strong>?</p>
                        <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Job ID: {jobData.jobId}</p>
                        <div className="delete-confirmation-buttons">
                            <button
                                className="cancel-delete-btn"
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={deleting}
                            >
                                Cancel
                            </button>
                            <button
                                className="confirm-delete-btn"
                                onClick={handleDeleteJob}
                                disabled={deleting}
                            >
                                {deleting ? 'Deleting...' : 'Delete Job'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobDetails;